import { Check, Pencil, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  FY_OPTIONS,
  formatCurrency,
  getCurrentFiscalQuarter,
} from "@/components/deal-metrics/shared";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatFY } from "@/lib/fiscal";

interface CompanyTarget {
  id: string;
  fiscalYear: number;
  metric: string;
  targetValue: number;
}

export function TargetsPage() {
  const { fy: currentFy } = getCurrentFiscalQuarter();
  const [selectedFy, setSelectedFy] = useState(currentFy);
  const [targets, setTargets] = useState<CompanyTarget[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/targets?fy=${selectedFy}`);
      if (res.ok) {
        const data = await res.json();
        setTargets(data.targets);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedFy]);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const bonusTarget = targets.find((t) => t.metric === "bonus_activation_revenue");

  const handleSave = async () => {
    const numValue = Number.parseFloat(editValue.replace(/,/g, ""));
    if (Number.isNaN(numValue) || numValue < 0) return;

    setSaving(true);
    try {
      const res = await fetch("/api/targets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fiscalYear: selectedFy,
          metric: "bonus_activation_revenue",
          targetValue: numValue,
        }),
      });
      if (res.ok) {
        await fetchTargets();
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title="Targets" description="Company-wide financial goals by fiscal year" />
        <Select value={String(selectedFy)} onValueChange={(v) => setSelectedFy(Number(v))}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FY_OPTIONS.map((fy) => (
              <SelectItem key={fy} value={String(fy)}>
                {formatFY(fy)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <p className="text-sm text-muted-foreground -mt-4">
        Apr {selectedFy - 1} – Mar {selectedFy}
      </p>

      <div>
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          Company Goals
        </h3>

        <Card className="p-6 max-w-md">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider">
            Bonus Activation Revenue
          </Label>

          {loading ? (
            <div className="h-8 mt-2 bg-muted rounded animate-pulse" />
          ) : editing ? (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-lg font-semibold text-muted-foreground">$</span>
              <Input
                type="text"
                inputMode="numeric"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="text-lg font-semibold"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSave();
                  if (e.key === "Escape") setEditing(false);
                }}
              />
              <Button size="icon" variant="ghost" onClick={handleSave} disabled={saving}>
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setEditing(false)}
                disabled={saving}
              >
                <X className="h-4 w-4 text-muted-foreground" />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-2xl font-semibold font-serif">
                {bonusTarget ? formatCurrency(bonusTarget.targetValue) : "Not set"}
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => {
                  setEditValue(bonusTarget ? String(bonusTarget.targetValue) : "");
                  setEditing(true);
                }}
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
